import React from 'react';
import {
  LineChart, BarList, StatTile, eventLabel,
  formatNumber, formatMinutes,
} from './analyticsUtils';

const TREND_SERIES = [
  { key: 'activeMentors', label: 'Active mentors', color: '#3b82f6' },
  { key: 'sessions', label: 'Sessions', color: '#a3e635' },
];

const VOLUME_SERIES = [
  { key: 'events', label: 'Events', color: '#3b82f6' },
  { key: 'foregroundHours', label: 'Foreground hours', color: '#f97316' },
];

const OverviewTrends = ({ data }) => {
  if (!data) return null;
  const { totals, daily, featureMix, appVersions, topScreens } = data;

  const features = featureMix
    .filter((f) => f.isFeature)
    .map((f) => ({ label: eventLabel(f.name), count: f.count, suffix: `${f.mentors} mentors` }));

  const lifecycle = featureMix
    .filter((f) => !f.isFeature)
    .map((f) => ({ label: eventLabel(f.name), count: f.count, suffix: `${f.mentors} mentors` }));

  const versions = appVersions.map((v) => ({
    label: `${v.appVersion} (${v.platform})`,
    count: v.mentors,
  }));

  const screens = topScreens.map((s) => ({
    label: s.screen,
    count: s.count,
    suffix: `${s.mentors} mentors`,
  }));

  return (
    <div className="analytics-overview">
      <div className="analytics-stat-row">
        <StatTile
          label="Active mentors"
          value={formatNumber(totals.activeMentors)}
          sub={`of ${formatNumber(totals.totalMentors)} — ${totals.adoptionRate}% adoption`}
          tone="primary"
        />
        <StatTile
          label="Not active in range"
          value={formatNumber(totals.dormantMentors)}
          sub="No app activity"
          tone={totals.dormantMentors > 0 ? 'warn' : undefined}
        />
        <StatTile label="Sessions" value={formatNumber(totals.sessions)} />
        <StatTile label="Events" value={formatNumber(totals.events)} />
        <StatTile
          label="Foreground time"
          value={`${formatNumber(totals.foregroundHours)}h`}
          sub="Total across all mentors"
        />
        <StatTile
          label="Avg session"
          value={formatMinutes(totals.avgSessionMinutes)}
          // ~28% of sessions never emit app_background, so their duration is
          // unknown; state the coverage rather than implying full measurement.
          sub={`Measured on ${totals.timedSessionCoverage}% of sessions`}
        />
      </div>

      <section className="analytics-card">
        <h3 className="analytics-card-title">Daily engagement</h3>
        <LineChart data={daily} series={TREND_SERIES} />
      </section>

      <section className="analytics-card">
        <h3 className="analytics-card-title">Volume &amp; time in app</h3>
        <LineChart data={daily} series={VOLUME_SERIES} />
      </section>

      <div className="analytics-grid-2">
        <section className="analytics-card">
          <h3 className="analytics-card-title">Feature usage</h3>
          <BarList items={features} emptyText="No feature events in this range" />
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">App lifecycle events</h3>
          <BarList items={lifecycle} emptyText="No lifecycle events in this range" />
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">Top screens</h3>
          <BarList items={screens} emptyText="No screen views in this range" />
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">App versions in use</h3>
          <p className="analytics-card-note">Mentors counted on their most recent build.</p>
          <BarList items={versions} emptyText="No version data" />
        </section>
      </div>
    </div>
  );
};

export default OverviewTrends;
